// Term text slot (S5): clause text with defined terms and references marked longest-first without overlaps; each mark is a link with a hover card (opened by pointer or keyboard focus, closed with Esc) and its definition as the accessible description; an optional term (the definition being read) still claims its span but stays plain text.
import { Link } from "@tanstack/react-router";
import { BookAIcon, FileTextIcon, LibraryIcon } from "lucide-react";
import { Fragment, useId, useMemo } from "react";

import type { DefinitionRef, ReferenceRef } from "@/api/__generated__";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cn } from "@/lib/cn";
import { docUidOf } from "@/lib/url";

import { findTermMarks, segmentText, type TermMark } from "./term-matching";

export interface TermTextProps {
  text: string;
  docUid: string;
  definitions: DefinitionRef[];
  references: ReferenceRef[];
  regionCode: string;
  className?: string;
  plainDefinitionUid?: string;
}

const MARK_BASE =
  "rounded-[2px] text-inherit outline-offset-1 transition-colors duration-120 focus-visible:bg-selected focus-visible:text-selected-foreground";
const DEFINITION_MARK = cn(
  MARK_BASE,
  "underline decoration-b2 decoration-dotted decoration-1 underline-offset-[3px] hover:bg-selected hover:text-selected-foreground",
);
const REFERENCE_MARK = cn(MARK_BASE, "font-medium text-brand-blue underline-offset-2 hover:underline");
const MAX_LISTED_TARGETS = 6;

function CardEyebrow({ icon: Icon, children }: { icon: typeof BookAIcon; children: string }) {
  return (
    <div className="flex items-center gap-1.5 text-label font-semibold tracking-caps text-muted-foreground uppercase">
      <Icon aria-hidden className="size-3.5 text-b2" />
      {children}
    </div>
  );
}

function DefinitionMark({
  mark,
  docUid,
  regionCode,
  descriptionId,
}: {
  mark: Extract<TermMark, { kind: "definition" }>;
  docUid: string;
  regionCode: string;
  descriptionId: string;
}) {
  const { definition } = mark;
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Link
          to="/$region/glossary"
          params={{ region: regionCode }}
          search={{ doc: docUid, term: definition.definition_uid }}
          aria-describedby={descriptionId}
          data-term-mark="definition"
          className={DEFINITION_MARK}
        >
          {mark.text}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent align="start" side="top" className="w-[340px]">
        <CardEyebrow icon={BookAIcon}>Defined term</CardEyebrow>
        <p className="m-0 mt-1.5 text-ui font-semibold text-foreground">{definition.term}</p>
        <p className="m-0 mt-1 line-clamp-[9] text-ui leading-relaxed text-ink-2">{definition.text}</p>
        <p className="m-0 mt-2.5 border-t border-border-subtle pt-2 text-xs text-muted-foreground">
          Opens the definition in the glossary
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

function referenceLabel(reference: ReferenceRef): string {
  return reference.number ?? reference.designation ?? reference.raw_text ?? reference.uid;
}

function ReferenceMark({
  mark,
  docUid,
  regionCode,
  descriptionId,
}: {
  mark: Extract<TermMark, { kind: "reference" }>;
  docUid: string;
  regionCode: string;
  descriptionId: string;
}) {
  const targets = [...mark.references].sort((left, right) =>
    referenceLabel(left).localeCompare(referenceLabel(right), "en-GB", { numeric: true }),
  );
  const first = targets[0];
  if (first === undefined) {
    return <>{mark.text}</>;
  }
  const isStandard = first.label === "ExternalStandard";
  const link = isStandard ? (
    <Link
      to="/$region/library/$docUid"
      params={{ region: regionCode, docUid }}
      search={{ tab: "standards" }}
      aria-describedby={descriptionId}
      data-term-mark="reference"
      className={REFERENCE_MARK}
    >
      {mark.text}
    </Link>
  ) : (
    <Link
      to="/$region/library/$docUid"
      params={{ region: regionCode, docUid: docUidOf(first.uid) }}
      search={{ tab: "contents", clause: first.uid }}
      aria-describedby={descriptionId}
      data-term-mark="reference"
      className={REFERENCE_MARK}
    >
      {mark.text}
    </Link>
  );
  return (
    <HoverCard>
      <HoverCardTrigger asChild>{link}</HoverCardTrigger>
      <HoverCardContent align="start" side="top" className="w-[320px]">
        <CardEyebrow icon={isStandard ? LibraryIcon : FileTextIcon}>
          {isStandard ? "External standard" : targets.length > 1 ? "Referenced clauses" : "Referenced clause"}
        </CardEyebrow>
        <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
          {targets.slice(0, MAX_LISTED_TARGETS).map((target) => (
            <li key={target.uid} className="min-w-0 text-ui">
              <span className="font-mono text-xs font-semibold text-brand-blue">
                {referenceLabel(target)}
              </span>
              {target.title ? <span className="ml-2 text-ink-2">{target.title}</span> : null}
            </li>
          ))}
        </ul>
        {targets.length > MAX_LISTED_TARGETS ? (
          <p className="m-0 mt-1 text-xs text-muted-foreground">
            and {targets.length - MAX_LISTED_TARGETS} more
          </p>
        ) : null}
        <p className="m-0 mt-2.5 border-t border-border-subtle pt-2 text-xs text-muted-foreground">
          {isStandard ? "Opens the standards cited by this document" : "Opens the clause in the library"}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

function description(mark: TermMark): string {
  if (mark.kind === "definition") {
    return `Defined term: ${mark.definition.text}`;
  }
  const labels = mark.references.map(referenceLabel);
  const standard = mark.references[0]?.label === "ExternalStandard";
  return standard ? `External standard ${labels.join(", ")}` : `Reference to ${labels.join(", ")}`;
}

export function TermText({
  text,
  docUid,
  definitions,
  references,
  regionCode,
  className,
  plainDefinitionUid,
}: TermTextProps) {
  const baseId = useId();
  const segments = useMemo(
    () => segmentText(text, findTermMarks(text, definitions, references)),
    [text, definitions, references],
  );
  return (
    <p className={cn("m-0 text-sm leading-relaxed whitespace-pre-line text-ink-2", className)}>
      {segments.map((segment) => {
        if (
          segment.kind === "text" ||
          (segment.kind === "definition" && segment.definition.definition_uid === plainDefinitionUid)
        ) {
          return <Fragment key={segment.key}>{segment.text}</Fragment>;
        }
        const descriptionId = `${baseId}-${segment.key}`;
        return (
          <span key={segment.key}>
            {segment.kind === "definition" ? (
              <DefinitionMark
                mark={segment}
                docUid={docUid}
                regionCode={regionCode}
                descriptionId={descriptionId}
              />
            ) : (
              <ReferenceMark
                mark={segment}
                docUid={docUid}
                regionCode={regionCode}
                descriptionId={descriptionId}
              />
            )}
            <span id={descriptionId} hidden>
              {description(segment)}
            </span>
          </span>
        );
      })}
    </p>
  );
}
